import React, { useState } from 'react';
import { AlertCircle, Key, User, Shield, CheckCircle, X, GraduationCap } from 'lucide-react';

import '../styles/StudentPasswordReset.css';

const StudentPasswordReset = () => {
    const [emailId, setEmailId] = useState('');
    const [studentId, setStudentId] = useState('');
    const [resetOption, setResetOption] = useState('email'); // 'email' or 'studentId'
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        const currentValue = resetOption === 'email' ? emailId : studentId;
        if (!currentValue.trim()) {
            setError(`${resetOption === 'email' ? 'Email ID' : 'Student ID'} is required`);
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            // Get auth token from localStorage or context
            const token = localStorage.getItem('token'); // Adjust based on your auth implementation

            const requestBody = {};
            if (resetOption === 'email') {
                requestBody.email_Id = emailId;
            } else {
                requestBody.studentId = studentId;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/student/reset-default-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Adjust based on your auth implementation
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (response.ok) {
                setResult({
                    success: true,
                    message: data.message,
                    student: data.student,
                    resetInfo: data.resetInfo
                });
                // Clear form
                setEmailId('');
                setStudentId('');
            } else {
                setError(data.message || 'Failed to reset student password');
            }
        } catch (err) {
            setError('Network error occurred. Please try again.');
            console.error('Student password reset error:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearMessages = () => {
        setResult(null);
        setError('');
    };

    const handleResetOptionChange = (option) => {
        setResetOption(option);
        setEmailId('');
        setStudentId('');
        clearMessages();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    const currentValue = resetOption === 'email' ? emailId : studentId;
    const isFormValid = currentValue.trim();

    return (
        <div className="student-password-reset-container">
            <div className="student-password-reset-wrapper">
                {/* Header */}
                <div className="student-header-card">
                    <div className="student-header-content">
                        <GraduationCap className="student-header-icon" size={24} />
                        <h1 className="student-header-title">Student Password Reset</h1>
                    </div>
                    <p className="student-header-description">
                        Reset student passwords to default value (Super-Admin & Branch-Head only)
                    </p>
                </div>

                {/* Warning Notice */}
                <div className="student-warning-notice">
                    <AlertCircle className="student-warning-icon" size={20} />
                    <div>
                        <h3 className="student-warning-title">Security Warning</h3>
                        <p className="student-warning-text">
                            This action will reset the student's password to their Student ID and force them to set up a new password on next login.
                            Use this feature responsibly and only when necessary.
                        </p>
                    </div>
                </div>

                {/* Reset Options */}
                <div className="student-options-card">
                    <h3 className="student-options-title">Search Student By:</h3>
                    <div className="student-options-buttons">
                        <button
                            onClick={() => handleResetOptionChange('email')}
                            className={`student-option-button ${resetOption === 'email' ? 'student-option-button-active' : ''}`}
                            disabled={loading}
                        >
                            <User size={18} />
                            Email ID
                        </button>
                        <button
                            onClick={() => handleResetOptionChange('studentId')}
                            className={`student-option-button ${resetOption === 'studentId' ? 'student-option-button-active' : ''}`}
                            disabled={loading}
                        >
                            <GraduationCap size={18} />
                            Student ID
                        </button>
                    </div>
                </div>

                {/* Reset Form */}
                <div className="student-form-card">
                    <div className="student-form-content">
                        <div className="student-input-group">
                            <label htmlFor="searchInput" className="student-input-label">
                                {resetOption === 'email' ? 'Email ID' : 'Student ID'}
                            </label>
                            <div className="student-input-wrapper">
                                {resetOption === 'email' ? (
                                    <User className="student-input-icon" size={20} />
                                ) : (
                                    <GraduationCap className="student-input-icon" size={20} />
                                )}
                                <input
                                    type="text"
                                    id="searchInput"
                                    value={resetOption === 'email' ? emailId : studentId}
                                    onChange={(e) => {
                                        if (resetOption === 'email') {
                                            setEmailId(e.target.value);
                                        } else {
                                            setStudentId(e.target.value);
                                        }
                                        clearMessages();
                                    }}
                                    onKeyPress={handleKeyPress}
                                    className="student-input-field"
                                    placeholder={`Enter student ${resetOption === 'email' ? 'email ID' : 'ID'}`}
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={loading || !isFormValid}
                            className="student-submit-button"
                        >
                            {loading ? (
                                <>
                                    <div className="student-loading-spinner"></div>
                                    Resetting Password...
                                </>
                            ) : (
                                <>
                                    <Key size={20} />
                                    Reset to Default Password
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Success Message */}
                {result && result.success && (
                    <div className="student-success-message">
                        <div className="student-success-content">
                            <CheckCircle className="student-success-icon" size={20} />
                            <div className="student-success-body">
                                <h3 className="student-success-title">Password Reset Successful</h3>
                                <p className="student-success-text">{result.message}</p>
                                
                                {result.student && (
                                    <div className="student-success-details">
                                        <div className="student-success-detail-row">
                                            <span className="student-success-detail-label">Student Name:</span>
                                            <code className="student-success-detail-value">{result.student.name}</code>
                                        </div>
                                        <div className="student-success-detail-row">
                                            <span className="student-success-detail-label">Student ID:</span>
                                            <code className="student-success-detail-value">{result.student.studentId}</code>
                                        </div>
                                        <div className="student-success-detail-row">
                                            <span className="student-success-detail-label">Email ID:</span>
                                            <code className="student-success-detail-value">{result.student.email_Id}</code>
                                        </div>
                                        {result.resetInfo && result.resetInfo.defaultPassword && (
                                            <div className="student-success-detail-row">
                                                <span className="student-success-detail-label">Default Password:</span>
                                                <code className="student-success-detail-value">{result.resetInfo.defaultPassword}</code>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <p className="student-success-note">
                                    The student will be required to set up a new password on next login using their Student ID.
                                </p>
                            </div>
                            <button
                                onClick={clearMessages}
                                className="student-close-button"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="student-error-message">
                        <div className="student-error-content">
                            <AlertCircle className="student-error-icon" size={20} />
                            <div className="student-error-body">
                                <h3 className="student-error-title">Error</h3>
                                <p className="student-error-text">{error}</p>
                            </div>
                            <button
                                onClick={clearMessages}
                                className="student-error-close-button"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Security Information */}
                <div className="student-security-info">
                    <div className="student-security-content">
                        <Shield className="student-security-icon" size={20} />
                        <div>
                            <h3 className="student-security-title">Security Information</h3>
                            <ul className="student-security-list">
                                <li>• Only Super-Admin and Branch-Head users can perform student password resets</li>
                                <li>• Default password is set to the student's <code className="student-security-code">Student ID</code></li>
                                <li>• Students can search by either Email ID or Student ID</li>
                                <li>• Students will be forced to set up a new password on next login</li>
                                <li>• All password reset actions are logged for security auditing</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentPasswordReset;